import { RouteSheet } from '@/components/route-sheet';
import { useGetMatchedRoute } from '@/hooks/routing';
import { type AssociationType } from '@/portal-api.definition';
import { Check } from '@nosinovacao/fe-icons';
import { useI18n } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { type ReactNode } from 'react';

export const Route = createFileRoute('/(services)/services/_sheet')({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const match = useGetMatchedRoute();
  const locationState = useLocation({ select: (loc) => loc.state }) as Record<string, unknown>;
  let hasBack = false;
  let backAsRouterNavigateBack = false;
  let title = '';
  let subtitle: string | undefined;
  let icon: ReactNode | undefined;
  const { t } = useI18n();
  switch (match.routeId) {
    case '/(services)/services/_sheet/users/':
      title = t('Services.Users.Title');
      subtitle = t('Services.Users.Subtitle');
      break;
    case '/(services)/services/_sheet/default-household/':
      title = t('Services.MainAddress.Title');
      break;
    case '/(services)/services/_sheet/apps/':
      title = t('Apps.Title');
      break;
    case '/(services)/services/_sheet/switch-services/':
      title = t('Services.AddServices.Title');
      subtitle = t('Services.AddServices.Subtitle');
      break;
    case '/(services)/services/_sheet/switch-services/holder-input/':
      hasBack = true;
      backAsRouterNavigateBack = true;
      title = t(
        `Services.AddServices.HolderInput.Title.${(locationState?.associationType as AssociationType) ?? 'Nif'}`,
      );
      break;
    case '/(services)/services/_sheet/switch-services/contact-selection/':
      hasBack = true;
      backAsRouterNavigateBack = true;
      title = t('Services.AddServices.ContactSelection.Title');
      break;
    case '/(services)/services/_sheet/switch-services/code-input/': {
      hasBack = true;
      backAsRouterNavigateBack = true;
      title = t('Services.AddServices.CodeInput.Title');
      const assocType = locationState?.associationType as AssociationType | undefined;
      if (assocType) {
        let subtitleKey = `Services.AddServices.CodeInput.Subtitle.${assocType}`;
        if (assocType === 'Nif' || assocType === 'Passport') {
          const contacts = locationState?.contactSelectionContacts as
            | { Id: number; Type: string }[]
            | undefined;
          const contactId = locationState?.contactId as number | undefined;
          const contactType = contacts?.find((ct) => ct.Id === contactId)?.Type;
          if (contactType) subtitleKey += `.${contactType}`;
        }
        subtitle = t(subtitleKey, {
          contact: (locationState?.selectedContact as string) ?? '',
        });
      }
      break;
    }
    case '/(services)/services/_sheet/switch-services/success/':
      title = t('Services.AddServices.Success.Title');
      subtitle = t('Services.AddServices.Success.Subtitle');
      icon = <Check width={48} height={48} className="text-nos-green" />;
      break;
    case '/(services)/services/_sheet/users/add/':
      hasBack = true;
      backAsRouterNavigateBack = true;
      title = t('Services.Users.Add.Title');
      subtitle = t('Services.Users.Add.Subtitle');
      break;
    case '/(services)/services/_sheet/users/invite-success/':
      title = '';
      break;
    case '/(services)/services/_sheet/users/$userId/':
      hasBack = true;
      backAsRouterNavigateBack = false;
      title = '';
      break;
  }

  return (
    <RouteSheet
      onClose={() => navigate({ to: '/services' })}
      options={{ hasBack, backAsRouterNavigateBack, title, subtitle, icon }}
    />
  );
}
